import { createContext, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { setViewAs, supabase } from '@/lib/supabase';
import { runSessionReset } from './sessionReset';
import { applyPreview, viewerIdOf, type PreviewIdentity } from './previewAs';
import { resolveAccess, type AuthState, type ProfileRow, type Role } from './resolveAccess';

/** `me.role` là vai trò ĐANG XEM (đổi theo "Xem như"); `actualRole` là vai trò thật của phiên (chỉ có khi đã đăng nhập). */
export type AuthValue = AuthState & {
  signOut(): Promise<void>; retry(): void;
  actualRole?: Role | null; preview?: PreviewIdentity | null; setPreview?(p: PreviewIdentity | null): void;
  /** Người ĐANG XEM (đổi theo "Xem như"): dùng cho truy vấn đọc "của tôi". `me.id` vẫn là phiên thật — ghi/RLS/Edge dùng nó. */
  viewerId?: string | null; isPreviewing?: boolean;
};
export const AuthContext = createContext<AuthValue | null>(null);

// Ném khi không đọc được phiên/hồ sơ; chỉ một truy vấn THÀNH CÔNG mà không có dòng mới là `denied`.
async function loadState(): Promise<AuthState> {
  const { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  const user = data.session?.user;
  if (!user) return { status: 'signedOut' };
  const { data: profile, error: profileError } = await supabase
    .from('profiles').select('id, email, name, role, active').eq('id', user.id).maybeSingle<ProfileRow>();
  if (profileError) throw profileError;
  return resolveAccess(profile ?? null, user.email ?? '');
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });
  const [preview, setPreview] = useState<PreviewIdentity | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;
  // Mỗi lần nạp lấy một số tăng dần; kết quả của lần cũ hơn lần mới nhất bị bỏ (kể cả sau signOut).
  const requestId = useRef(0);
  const refreshRef = useRef<() => void>(() => {});
  const queryClient = useQueryClient();
  const userRef = useRef<string | null>(null);
  const wipeCaches = useCallback(() => { setViewAs(null); queryClient.clear(); runSessionReset(); setPreview(null); }, [queryClient]);
  // Bật: ghi nhật ký (chưa có header) → gắn header → xoá cache. Tắt: bỏ header ngay → xoá cache. Ghi nhật ký lỗi thì không bật.
  const changePreview = useCallback((p: PreviewIdentity | null) => {
    void (async () => {
      if (p) {
        const { error } = await supabase.rpc('log_view_as', { p_target: p.id });
        if (error) { toast.error('Không bật được chế độ Xem như. Thử lại sau ít phút.'); return; }
        setViewAs(p.id);
      } else {
        setViewAs(null);
      }
      queryClient.clear();
      setPreview(p);
    })();
  }, [queryClient]);

  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const refresh = async () => {
      const id = ++requestId.current;
      let next: AuthState;
      try {
        next = await loadState();
      } catch {
        next = { status: 'error' };
      }
      if (!alive || id !== requestId.current) return;
      // Người đang dùng đổi (hoặc mất phiên): dữ liệu đã tải của người cũ không được lộ sang người sau.
      const nextUser = next.status === 'ready' ? next.me.id : null;
      if (next.status !== 'error' && next.status !== 'loading') {
        if (userRef.current !== null && userRef.current !== nextUser) wipeCaches();
        userRef.current = nextUser;
      }
      // giữ trạng thái denied cho tới khi người dùng thử email khác
      setState((prev) => (prev.status === 'denied' && next.status === 'signedOut' ? prev : next));
      // Không để phiên của người không được phép sống tiếp (spec §3.1). Đặt denied TRƯỚC khi đăng xuất
      // để sự kiện SIGNED_OUT do chính việc này gây ra không xoá mất thông báo từ chối.
      if (next.status === 'denied') await supabase.auth.signOut();
    };
    refreshRef.current = () => { void refresh(); };
    void refresh();
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (event !== 'SIGNED_IN' && event !== 'SIGNED_OUT' && event !== 'USER_UPDATED') return;
      // Lấy lại tiêu điểm cửa sổ bắn lại SIGNED_IN cho cùng một người: không cần nạp lại.
      const current = stateRef.current;
      if (event === 'SIGNED_IN' && current.status === 'ready' && session?.user.id === current.me.id) return;
      // Hoãn ra khỏi callback: gọi lại supabase.auth ngay trong callback có thể treo (khoá nội bộ của supabase-js).
      clearTimeout(timer);
      timer = setTimeout(() => { void refresh(); }, 0);
    });
    return () => { alive = false; clearTimeout(timer); data.subscription.unsubscribe(); };
  }, [wipeCaches]);

  const signOut = useCallback(async () => {
    requestId.current += 1; // vô hiệu mọi lần nạp đang bay
    await supabase.auth.signOut();
    requestId.current += 1;
    userRef.current = null;
    wipeCaches();
    setState({ status: 'signedOut' });
  }, [wipeCaches]);

  const retry = useCallback(() => {
    setState({ status: 'loading' });
    refreshRef.current();
  }, []);

  const actualRole = state.status === 'ready' ? state.me.role : null;
  const activePreview = actualRole === 'admin' ? preview : null;
  const value: AuthValue = {
    ...applyPreview(state, preview), signOut, retry, actualRole, preview: activePreview, setPreview: changePreview,
    viewerId: viewerIdOf(state, preview), isPreviewing: activePreview !== null,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
